import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { Alert, Button, Snackbar } from "@mui/material";
import { usePackagesActions } from "@/store";
import { decideShowAutoPausedPopup } from "./decideAutoPausedPopup";

/**
 * Rate-limited "auto-paused — resume?" popup (bug 2026-08 follow-up).
 *
 * Silent auto-resume (AutoResumeWatcher) handles transient dips without ever
 * involving the user. When silent resume can't recover — connection stayed
 * poor for > 60s — this component shows one Snackbar with a Resume action.
 * localStorage-backed cool-down prevents re-nag for the same uploadId
 * within 30 minutes so users on genuinely bad WiFi aren't bombarded.
 *
 * All the show/hide policy lives in `decideShowAutoPausedPopup` (pure,
 * unit-tested). This component is just the presentation + persistence.
 */

const STORAGE_KEY = "clipn:auto-paused-popup-last-shown-v1";
const HEARTBEAT_MS = 10_000;   // re-evaluate every 10s so the popup fires
                                // shortly after minPausedMs elapses even
                                // without a Redux state change

const loadLastShownMap = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Map();
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return new Map(Object.entries(parsed));
  } catch (_error) {
    /* fall through */
  }
  return new Map();
};

const persistLastShownMap = (map) => {
  try {
    const obj = {};
    for (const [k, v] of map.entries()) obj[k] = v;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch (_error) {
    /* localStorage may be full or blocked in private mode — non-fatal */
  }
};

export const AutoPausedNoticeManager = () => {
  const uploadingPackages = useSelector(
    (state) => state.packages.uploadingPackages,
    shallowEqual
  );
  const { resumeUpload } = usePackagesActions();

  const [visibleUploadId, setVisibleUploadId] = useState(null);
  const lastShownAtRef = useRef(loadLastShownMap());

  const evaluate = useCallback(() => {
    if (visibleUploadId) return; // one snackbar at a time
    const uploadId = decideShowAutoPausedPopup(uploadingPackages, {
      now: Date.now(),
      lastShownAtByUploadId: lastShownAtRef.current,
    });
    if (uploadId) {
      lastShownAtRef.current.set(uploadId, Date.now());
      persistLastShownMap(lastShownAtRef.current);
      setVisibleUploadId(uploadId);
    }
  }, [uploadingPackages, visibleUploadId]);

  // Re-evaluate on any packages-state change (immediate) and via a
  // heartbeat (so the popup fires when minPausedMs elapses without any
  // other Redux action to trigger a re-render).
  useEffect(() => {
    evaluate();
  }, [evaluate]);

  useEffect(() => {
    const id = setInterval(evaluate, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [evaluate]);

  // If the visible upload leaves the AUTO-paused set (user resumed, or
  // silent resume kicked in), dismiss the snackbar automatically.
  useEffect(() => {
    if (!visibleUploadId) return;
    const stillEligible = (uploadingPackages || []).some(
      (u) =>
        u.uploadId === visibleUploadId &&
        u.status === "PAUSED" &&
        u.pauseReason === "AUTO"
    );
    if (!stillEligible) setVisibleUploadId(null);
  }, [uploadingPackages, visibleUploadId]);

  const handleResume = () => {
    if (!visibleUploadId) return;
    try {
      resumeUpload(visibleUploadId);
    } catch (_error) {
      /* best-effort */
    }
    setVisibleUploadId(null);
  };

  const handleClose = () => setVisibleUploadId(null);

  if (!visibleUploadId) return null;

  return (
    <Snackbar
      open
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      onClose={(_e, reason) => {
        // Don't auto-dismiss on clickaway — user should explicitly
        // resume or close to acknowledge.
        if (reason === "clickaway") return;
        handleClose();
      }}
      data-testid="auto-paused-snackbar"
    >
      <Alert
        severity="warning"
        variant="filled"
        onClose={handleClose}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={handleResume}
            data-testid="auto-paused-resume"
          >
            Resume
          </Button>
        }
        sx={{ width: "100%", alignItems: "center" }}
      >
        Your upload was paused because the connection dropped. Ready to
        resume when you are.
      </Alert>
    </Snackbar>
  );
};

export default AutoPausedNoticeManager;
